import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  User as FirebaseUser,
  onAuthStateChanged,
  UserCredential,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db, googleProvider, facebookProvider, twitterProvider } from '../config/firebase';
import { UserMetadataService } from './userMetadataService';
import { EmailNotificationService } from './emailNotificationService';

export interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL?: string | null;
  role: 'owner' | 'admin' | 'trader' | 'user';
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
  createdAt: Date;
  lastLoginAt?: Date;
  approvedBy?: string;
  approvedAt?: Date;
  rejectionReason?: string;
  kycDocuments?: {
    nationalIdPassport: {
      type: 'national_id' | 'passport';
      number: string;
      issueDate: string;
      expiryDate: string;
    };
    licenseGovernmentCard: {
      type: 'license' | 'government_card';
      number: string;
      issueDate: string;
      expiryDate: string;
    };
    taxId: {
      type: 'pan' | 'ssn' | 'tax_id' | 'other';
      number: string;
      issueDate: string;
      expiryDate: string;
    };
  };
  kycSubmittedAt?: Date;
  // User metadata for tracking
  metadata?: {
    signup?: {
      ipAddress?: string;
      browser: string;
      operatingSystem: string;
      timestamp: Date;
      country?: string;
      city?: string;
      town?: string;
      userAgent: string;
      screenResolution: string;
      timezone: string;
      language: string;
    };
    lastLogin?: {
      ipAddress?: string;
      browser: string;
      operatingSystem: string;
      timestamp: Date;
      country?: string;
      city?: string;
      town?: string;
      userAgent: string;
      screenResolution: string;
      timezone: string;
      language: string;
    };
  };
  // Login attempt tracking
  loginAttempts?: {
    count: number;
    lastAttempt: Date;
    blockedUntil?: Date;
  };
}

export class FirebaseAuthService {
  // Register a new user
  static async register(
    email: string,
    password: string,
    displayName: string
  ): Promise<User> {
    try {
      // Validate password strength
      const passwordValidation = UserMetadataService.validatePasswordStrength(password);
      if (!passwordValidation.isStrong) {
        throw new Error('Password is too weak. Please use a stronger password.');
      }

      const userCredential: UserCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      const user = userCredential.user;

      // Update profile with display name
      await updateProfile(user, {
        displayName: displayName
      });

      // Collect user metadata
      const metadata = await UserMetadataService.collectUserMetadata();

      // Create user document in Firestore
      const userData: User = {
        uid: user.uid,
        email: user.email,
        displayName: displayName,
        photoURL: user.photoURL,
        role: 'user',
        status: 'pending',
        createdAt: new Date(),
        lastLoginAt: new Date(),
        metadata: {
          signup: metadata
        },
        loginAttempts: {
          count: 0,
          lastAttempt: new Date()
        }
      };

      await setDoc(doc(db, 'users', user.uid), userData);

      // Send welcome email
      try {
        await EmailNotificationService.sendWelcomeEmail(email, displayName);
      } catch (emailError) {
        console.warn('Failed to send welcome email:', emailError);
      }

      return userData;
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  // Login user
  static async login(email: string, password: string): Promise<User> {
    try {
      // First authenticate the user with Firebase Auth
      const userCredential: UserCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      const user = userCredential.user;

      // Now that user is authenticated, query Firestore
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        throw new Error('User for this email not found or invalid email');
      }

      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data() as User;

      // Check if account is suspended
      if (userData.status === 'suspended') {
        throw new Error('ACCOUNT_SUSPENDED');
      }

      // Check if account is blocked due to too many login attempts
      if (userData.loginAttempts?.blockedUntil && 
          new Date() < userData.loginAttempts.blockedUntil) {
        const remainingTime = Math.ceil((userData.loginAttempts.blockedUntil.getTime() - new Date().getTime()) / 1000 / 60);
        throw new Error(`Too many failed attempts. Please try again in ${remainingTime} minutes.`);
      }

      // Collect login metadata
      const loginMetadata = await UserMetadataService.collectUserMetadata();

      // Reset login attempts on successful login
      const updatedUserData = {
        lastLoginAt: new Date(),
        metadata: {
          ...userData.metadata,
          lastLogin: loginMetadata
        },
        loginAttempts: {
          count: 0,
          lastAttempt: new Date()
        }
      };

      await updateDoc(doc(db, 'users', user.uid), updatedUserData);

      return {
        ...userData,
        ...updatedUserData
      };
    } catch (error: any) {
      // Handle specific error cases
      if (error.message === 'ACCOUNT_SUSPENDED') {
        throw error;
      }

      // Update login attempts for failed login
      if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        await this.updateFailedLoginAttempts(email);
      }

      throw new Error(this.getErrorMessage(error.code));
    }
  }

  // Logout user
  static async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error: any) {
      throw new Error('Logout failed');
    }
  }

  // Reset password
  static async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  // Get current user
  static getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  }

  // Listen to auth state changes
  static onAuthStateChanged(callback: (user: FirebaseUser | null) => void) {
    return onAuthStateChanged(auth, callback);
  }

  // Get user data from Firestore
  static async getUserData(uid: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return userDoc.data() as User;
      }
      return null;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  }

  // Update user profile
  static async updateUserProfile(uid: string, updates: Partial<User>): Promise<void> {
    try {
      await setDoc(doc(db, 'users', uid), updates, { merge: true });
    } catch (error: any) {
      throw new Error('Failed to update profile');
    }
  }

  // Update failed login attempts
  private static async updateFailedLoginAttempts(email: string): Promise<void> {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data() as User;
        const currentAttempts = userData.loginAttempts?.count || 0;
        const newAttempts = currentAttempts + 1;
        
        let blockedUntil: Date | undefined;
        if (newAttempts >= 5) {
          // Block for 30 minutes after 5 failed attempts
          blockedUntil = new Date(Date.now() + 30 * 60 * 1000);
        }
        
        await updateDoc(doc(db, 'users', userDoc.id), {
          loginAttempts: {
            count: newAttempts,
            lastAttempt: new Date(),
            blockedUntil
          }
        });
      }
    } catch (error) {
      console.error('Failed to update login attempts:', error);
    }
  }

  // Convert Firebase error codes to user-friendly messages
  private static getErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'An account with this email already exists';
      case 'auth/invalid-email':
        return 'Invalid email address';
      case 'auth/operation-not-allowed':
        return 'Email/password accounts are not enabled';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters';
      case 'auth/user-disabled':
        return 'This account has been disabled';
      case 'auth/user-not-found':
        return 'User for this email not found or invalid email';
      case 'auth/wrong-password':
        return 'Password is incorrect';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection';
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled';
      case 'auth/popup-blocked':
        return 'Sign-in popup was blocked. Please allow popups for this site';
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with the same email address but different sign-in credentials';
      case 'auth/requires-recent-login':
        return 'Please sign in again to complete this action';
      default:
        return '';
    }
  }

  // Social authentication methods
  static async signInWithGoogle(): Promise<User> {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const userData = await this.handleSocialSignIn(result);
      return userData;
    } catch (error: any) {
      // Fallback to redirect if popup fails
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
        await signInWithRedirect(auth, googleProvider);
        return {} as User; // Will be handled by redirect result
      }
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  static async signInWithFacebook(): Promise<User> {
    try {
      const result = await signInWithPopup(auth, facebookProvider);
      const userData = await this.handleSocialSignIn(result);
      return userData;
    } catch (error: any) {
      // Fallback to redirect if popup fails
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
        await signInWithRedirect(auth, facebookProvider);
        return {} as User; // Will be handled by redirect result
      }
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  static async signInWithTwitter(): Promise<User> {
    try {
      const result = await signInWithPopup(auth, twitterProvider);
      const userData = await this.handleSocialSignIn(result);
      return userData;
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  static async signInWithInstagram(): Promise<User> {
    try {
      // Instagram uses Facebook authentication
      const result = await signInWithPopup(auth, facebookProvider);
      const userData = await this.handleSocialSignIn(result);
      return userData;
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  // Handle OAuth redirect results
  static async handleRedirectResult(): Promise<User | null> {
    try {
      const result = await getRedirectResult(auth);
      if (result) {
        return await this.handleSocialSignIn(result);
      }
      return null;
    } catch (error: any) {
      console.error('Error handling redirect result:', error);
      return null;
    }
  }

  // Handle social sign-in result
  private static async handleSocialSignIn(result: UserCredential): Promise<User> {
    const { user: firebaseUser } = result;
    
    // Check if user already exists in our database
    let userData = await this.getUserData(firebaseUser.uid);
    
    if (!userData) {
      // Create new user in our database
      const newUser: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        role: 'user',
        status: 'pending',
        createdAt: new Date(),
        lastLoginAt: new Date(),
        metadata: {
          signup: {
            browser: navigator.userAgent,
            operatingSystem: navigator.platform,
            timestamp: new Date(),
            userAgent: navigator.userAgent,
            screenResolution: typeof window !== 'undefined' && window.screen ? `${window.screen.width}x${window.screen.height}` : 'unknown',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: navigator.language
          }
        }
      };
      
      await setDoc(doc(db, 'users', firebaseUser.uid), newUser);
      userData = newUser;
    } else {
      // Update last login
      await this.updateUserProfile(firebaseUser.uid, {
        lastLoginAt: new Date(),
        metadata: {
          ...userData.metadata,
          lastLogin: {
            browser: navigator.userAgent,
            operatingSystem: navigator.platform,
            timestamp: new Date(),
            userAgent: navigator.userAgent,
            screenResolution: typeof window !== 'undefined' && window.screen ? `${window.screen.width}x${window.screen.height}` : 'unknown',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: navigator.language
          }
        }
      });
    }
    
    return userData;
  }
}
